// Standalone cone/tube builder studio. Reuses the exact geometry/camera/
// lighting/logo constants already tuned on the product-page 3D size chart
// (assets/product-size-chart-3d.js), reparameterized to run off the
// studio's own shape/size/color selection instead of one product's fixed
// dataset - and adds quantity/pricing + a quote-request submit flow this
// engine doesn't need.
(function () {
  function whenThreeReady(cb) {
    if (window.THREE) { cb(); return; }
    var tries = 0;
    var timer = setInterval(function () {
      tries++;
      if (window.THREE) { clearInterval(timer); cb(); }
      else if (tries > 100) { clearInterval(timer); }
    }, 50);
  }

  function init(root) {
    var dataEl = root.querySelector('[data-cone-studio-data]');
    if (!dataEl) return;
    var data;
    try { data = JSON.parse(dataEl.textContent); } catch (e) { return; }

    var canvas = root.querySelector('[data-cone-studio-canvas]');
    var badge = root.querySelector('[data-cone-studio-badge]');
    var badgeName = root.querySelector('[data-badge-name]');
    var badgeSub = root.querySelector('[data-badge-sub]');
    var typeRow = root.querySelector('[data-studio-type-row]');
    var sizeRow = root.querySelector('[data-studio-size-row]');
    var tipSection = root.querySelector('[data-studio-tip-section]');
    var tipRow = root.querySelector('[data-studio-tip-row]');
    var colorGrid = root.querySelector('[data-studio-color-grid]');
    var colorTitleEl = root.querySelector('[data-studio-color-title]');
    var bandSection = root.querySelector('[data-studio-band-section]');
    var bgRow = root.querySelector('[data-studio-bg-row]');
    var qtyGrid = document.querySelector('[data-studio-quantity-grid]'); // lives inside the quote modal, not root
    var ctaBtn = root.querySelector('[data-studio-open-quote]');
    var exportBtn = root.querySelector('[data-cone-studio-export]');

    var THREE = window.THREE;

    // ---- state ----
    var state = {
      shape: data.cone.length ? 'cone' : 'tube',
      length: null,
      color: null,
      tipType: null,
      option: null,
      bandColor: 'black',
      // Jars don't fit the cone/tube color model (one product = one fixed
      // paper color from a shared palette) - each jar SKU instead lists its
      // own real Jar-color + Cap-color combos, so the selection is stored
      // as its own {jar, cap} pair here rather than reusing state.color.
      jarCombo: null,
      background: 'dark',
      qty: null,
      pricePerUnit: null
    };

    var TIP_TYPE_LABELS = { glass: 'Glass', wood: 'Wood', spiral: 'Spiral', ceramic: 'Ceramic', standard: 'Standard' };
    var TIP_TYPE_ORDER = ['glass', 'wood', 'ceramic', 'spiral', 'standard'];

    function optionsForShape(shape) { return data[shape] || []; }
    function lengthsForShape(shape) {
      var seen = {}, out = [];
      optionsForShape(shape).forEach(function (o) {
        if (!seen[o.length]) { seen[o.length] = true; out.push(o.length); }
      });
      out.sort(function (a, b) { return parseFloat(a) - parseFloat(b); });
      return out;
    }
    function colorsForShapeLength(shape, length) {
      return optionsForShape(shape).filter(function (o) { return o.length === length; });
    }
    // Tubes only: real products at a given size can be Glass/Wood/Spiral/
    // Ceramic tips with genuinely different physical filter lengths, so
    // the tip type has to be its own selectable dimension - otherwise
    // switching color alone could silently swap in a different tip type
    // and the color band would jump position for no visible reason.
    function tipTypesForLength(shape, length) {
      if (shape !== 'tube') return [];
      var seen = {}, out = [];
      colorsForShapeLength(shape, length).forEach(function (o) {
        var tt = o.tip_type || 'standard';
        if (!seen[tt]) { seen[tt] = true; out.push(tt); }
      });
      // Ceramic is always offered on tubes, even at sizes with no ceramic
      // SKUs in the catalog yet - like colors, it's not gated on a real
      // product existing (see referenceOptionFor's fallback below for the
      // price/geometry side of this).
      if (!seen.ceramic) out.push('ceramic');
      out.sort(function (a, b) { return TIP_TYPE_ORDER.indexOf(a) - TIP_TYPE_ORDER.indexOf(b); });
      return out;
    }
    function colorsForTip(shape, length, tipType) {
      var list = colorsForShapeLength(shape, length);
      if (shape !== 'tube' || !tipType) return list;
      return list.filter(function (o) { return (o.tip_type || 'standard') === tipType; });
    }
    // A real product is only used as a PRICING/GEOMETRY reference now - it
    // no longer determines which colors are selectable. Any real product
    // at this shape+length+tip works as the reference (geometry doesn't
    // vary by paper color), so this just takes whichever one comes first.
    // Ceramic has no real SKUs yet, so it borrows Glass's reference product
    // at the same length (closest dimensionally), falling back to any real
    // product at that length if even Glass isn't stocked there.
    function referenceOptionFor(shape, length, tipType) {
      var direct = colorsForTip(shape, length, tipType)[0];
      if (direct) return direct;
      return colorsForTip(shape, length, 'glass')[0] || colorsForShapeLength(shape, length)[0] || null;
    }
    // The color grid is always the FULL palette from the Cone/Tube Color
    // metaobject - not filtered down to whatever colors happen to have a
    // real matching product at the current size/tip. That real-product
    // filtering was the actual cause of swatches re-shuffling/disappearing
    // every time Filter Tip changed; the palette is now a fixed list per
    // shape regardless of size or tip.
    function fullPaletteColors(shape) {
      return Object.keys(shape === 'tube' ? data.colorsTube : data.colorsCone);
    }
    // Jars: each real product is its own selectable "Size" tile (no shared
    // length to group by like cone/tube - diameters/heights/capacities
    // don't line up cleanly across Glass/Plastic/Concentrate jars), and its
    // combos string ("JarColor|CapColor;...") is parsed here rather than
    // in Liquid, since building nested JSON via string concatenation in
    // Liquid is error-prone.
    function jarSizeLabel(o) {
      var bits = [o.material, o.diameter];
      if (o.capacity) bits.push(o.capacity);
      else if (o.length) bits.push(o.length + ' H');
      return bits.join(' · ');
    }
    function parseCombos(str) {
      if (!str) return [];
      return str.split(';').filter(Boolean).map(function (pair) {
        var parts = pair.split('|');
        return { jar: parts[0], cap: parts[1] || parts[0] };
      });
    }
    var JAR_FINISH_HEX = {
      'Clear': '#eef5f6',
      'Matte Black': '#1b1b1b',
      'Matte White': '#f1efe9',
      'Glossy Black': '#0c0c0c',
      'Glossy White': '#ffffff',
      'Bamboo': '#c9a06b'
    };
    // ---- renderer / scene / camera (same tuned values as the product page) ----
    var renderer = new THREE.WebGLRenderer({ canvas: canvas, antialias: true, alpha: true, preserveDrawingBuffer: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    var scene = new THREE.Scene();
    var FOV_DEG = 32;
    var camera = new THREE.PerspectiveCamera(FOV_DEG, 1, 0.1, 100);
    var TOTAL_H = 6;
    var halfFovRad = (FOV_DEG * Math.PI / 180) / 2;
    var DEFAULT_Z = (TOTAL_H / 2 * 1.57) / Math.tan(halfFovRad);
    var MIN_Z = DEFAULT_Z * 0.65;
    var MAX_Z = DEFAULT_Z * 1.6;
    camera.position.set(0, 0, DEFAULT_Z);

    var ambientLight = new THREE.AmbientLight(0xffffff, 0.7);
    scene.add(ambientLight);
    var keyLight = new THREE.DirectionalLight(0xffffff, 0.4);
    keyLight.position.set(3, 4, 5);
    keyLight.castShadow = true;
    keyLight.shadow.mapSize.set(1024, 1024);
    keyLight.shadow.camera.near = 0.5;
    keyLight.shadow.camera.far = 20;
    keyLight.shadow.camera.left = -6;
    keyLight.shadow.camera.right = 6;
    keyLight.shadow.camera.top = 6;
    keyLight.shadow.camera.bottom = -6;
    keyLight.shadow.bias = -0.002;
    scene.add(keyLight);
    var fillLight = new THREE.DirectionalLight(0xffffff, 0.2);
    fillLight.position.set(-4, -1, 3);
    scene.add(fillLight);
    var rimLight = new THREE.DirectionalLight(0xffffff, 0.12);
    rimLight.position.set(0, 2, -5);
    scene.add(rimLight);

    // ---- ground shadow-catcher: a plane just below the model that only
    // ever renders its shadow, so the object reads as sitting on a real
    // surface instead of floating in a flat void, at any background color ----
    var groundGeo = new THREE.PlaneGeometry(24, 24);
    var groundMat = new THREE.ShadowMaterial({ opacity: 0.32 });
    var ground = new THREE.Mesh(groundGeo, groundMat);
    ground.rotation.x = -Math.PI / 2;
    ground.position.y = -TOTAL_H / 2 - 0.01;
    ground.receiveShadow = true;
    scene.add(ground);

    var group = new THREE.Group();
    scene.add(group);

    var BG_COLORS = { dark: 0x2b2e30, white: 0xffffff, warm: 0xe8e4dd };
    function applyBackground(key) {
      scene.background = new THREE.Color(BG_COLORS[key] || BG_COLORS.dark);
    }
    applyBackground(state.background);

    // ---- texture helpers (same as product page) ----
    var maxAniso = renderer.capabilities.getMaxAnisotropy();
    function sharpenTexture(tex) {
      tex.anisotropy = maxAniso;
      tex.minFilter = THREE.LinearMipmapLinearFilter;
      tex.magFilter = THREE.LinearFilter;
      tex.generateMipmaps = true;
      return tex;
    }
    function faceFrontTexture(tex) {
      tex.wrapS = THREE.RepeatWrapping;
      tex.offset.x = 0.5;
      return tex;
    }
    function makeSolidTexture(hex) {
      var c = document.createElement('canvas');
      c.width = 32; c.height = 32;
      var ctx = c.getContext('2d');
      ctx.fillStyle = hex;
      ctx.fillRect(0, 0, 32, 32);
      var tex = new THREE.CanvasTexture(c);
      tex.needsUpdate = true;
      return sharpenTexture(tex);
    }
    // Rolling-paper grain - soft mottled blotches (real paper's uneven
    // pulp/dye absorption) underneath a dense fibrous speckle (random short
    // strokes in every direction, unlike wood's uniform vertical streaks),
    // so the paper body clearly reads as a matte paper surface rather than
    // flat plastic, at any zoom level.
    // Offsets needed so a shape drawn near an edge also gets copies drawn
    // just outside the opposite edge - otherwise tex.repeat tiling shows a
    // hard seam every time the pattern repeats around the cylinder.
    function wrapOffsets(v, size, margin) {
      var offs = [0];
      if (v < margin) offs.push(size);
      if (v > size - margin) offs.push(-size);
      return offs;
    }
    function paintPaperGrain(ctx, size, darkStroke, lightStroke, blotchAlpha, fiberAlpha) {
      var i, j, k, x, y, r, len, ang, ox, oy, oxs, oys;
      // Large soft blotches first - uneven paper tone.
      for (i = 0; i < 160; i++) {
        x = Math.random() * size;
        y = Math.random() * size;
        r = size * (0.12 + Math.random() * 0.22);
        var shade = Math.random() > 0.5 ? darkStroke : lightStroke;
        oxs = wrapOffsets(x, size, r);
        oys = wrapOffsets(y, size, r);
        for (j = 0; j < oxs.length; j++) {
          for (k = 0; k < oys.length; k++) {
            ox = x + oxs[j];
            oy = y + oys[k];
            var grad = ctx.createRadialGradient(ox, oy, 0, ox, oy, r);
            grad.addColorStop(0, shade);
            grad.addColorStop(1, 'rgba(0,0,0,0)');
            ctx.globalAlpha = blotchAlpha;
            ctx.fillStyle = grad;
            ctx.beginPath();
            ctx.arc(ox, oy, r, 0, Math.PI * 2);
            ctx.fill();
          }
        }
      }
      // Dense fine fiber strokes on top.
      for (i = 0; i < 7200; i++) {
        x = Math.random() * size;
        y = Math.random() * size;
        len = size * (0.004 + Math.random() * 0.012);
        ang = Math.random() * Math.PI * 2;
        ctx.strokeStyle = Math.random() > 0.5 ? darkStroke : lightStroke;
        ctx.globalAlpha = fiberAlpha * (0.5 + Math.random());
        ctx.lineWidth = 0.7;
        oxs = wrapOffsets(x, size, len);
        oys = wrapOffsets(y, size, len);
        for (j = 0; j < oxs.length; j++) {
          for (k = 0; k < oys.length; k++) {
            ox = x + oxs[j];
            oy = y + oys[k];
            ctx.beginPath();
            ctx.moveTo(ox, oy);
            ctx.lineTo(ox + Math.cos(ang) * len, oy + Math.sin(ang) * len);
            ctx.stroke();
          }
        }
      }
      ctx.globalAlpha = 1;
    }
    function makePaperTexture(hex) {
      var c = document.createElement('canvas');
      c.width = 512; c.height = 512;
      var ctx = c.getContext('2d');
      ctx.fillStyle = hex;
      ctx.fillRect(0, 0, 512, 512);
      paintPaperGrain(ctx, 512, '#000000', '#ffffff', 0.014, 0.025);

      var tex = new THREE.CanvasTexture(c);
      tex.wrapS = THREE.RepeatWrapping;
      tex.wrapT = THREE.RepeatWrapping;
      tex.repeat.set(1, 1);
      tex.needsUpdate = true;
      return sharpenTexture(tex);
    }
    // Matching grayscale relief map for the same grain, used as a bumpMap so
    // the fibers/blotches pick up real light/shadow instead of only tinting
    // color - this is what actually sells the "paper" feel under lighting.
    var paperBumpTextureCache = null;
    function makePaperBumpTexture() {
      if (paperBumpTextureCache) return paperBumpTextureCache;
      var c = document.createElement('canvas');
      c.width = 512; c.height = 512;
      var ctx = c.getContext('2d');
      ctx.fillStyle = '#808080';
      ctx.fillRect(0, 0, 512, 512);
      paintPaperGrain(ctx, 512, '#000000', '#ffffff', 0.1, 0.13);

      var tex = new THREE.CanvasTexture(c);
      tex.wrapS = THREE.RepeatWrapping;
      tex.wrapT = THREE.RepeatWrapping;
      tex.repeat.set(1, 1);
      tex.needsUpdate = true;
      paperBumpTextureCache = sharpenTexture(tex);
      return paperBumpTextureCache;
    }
    // Procedural wood-grain texture (thin vertical streaks wrapping around
    // the cylinder = grain running along the tip's length, like a real
    // wood/cork filter tip) instead of a flat paper-colored cylinder.
    function makeWoodTexture() {
      var c = document.createElement('canvas');
      c.width = 128; c.height = 128;
      var ctx = c.getContext('2d');
      ctx.fillStyle = '#ddc49c';
      ctx.fillRect(0, 0, 128, 128);
      for (var i = 0; i < 60; i++) {
        var x = Math.random() * 128;
        var w = 0.6 + Math.random() * 1.8;
        ctx.fillStyle = Math.random() > 0.5 ? '#c7a878' : '#ecdcbc';
        ctx.globalAlpha = 0.15 + Math.random() * 0.28;
        ctx.fillRect(x, 0, w, 128);
      }
      ctx.globalAlpha = 1;
      var tex = new THREE.CanvasTexture(c);
      tex.needsUpdate = true;
      return sharpenTexture(tex);
    }
    // Real tip materials keyed off the product's actual tip type - glass
    // tips get a transmissive/clearcoat material, wood tips get a wood-
    // grain texture, ceramic gets a smooth glossy finish; spiral/standard
    // tips are genuinely rolled paper in real life, so they keep using
    // the plain paper-colored cylinder, which is already correct for them.
    function makeTipMaterial(hex, tipType) {
      if (tipType === 'glass') {
        return new THREE.MeshPhysicalMaterial({
          color: 0xf3efe6,
          metalness: 0,
          roughness: 0.15,
          transmission: 0.55,
          thickness: 0.5,
          ior: 1.5,
          clearcoat: 0.6,
          clearcoatRoughness: 0.1,
          transparent: true,
          opacity: 1,
          side: THREE.FrontSide
        });
      }
      if (tipType === 'wood') {
        return new THREE.MeshStandardMaterial({ map: makeWoodTexture(), roughness: 0.78, metalness: 0.02, side: THREE.DoubleSide });
      }
      if (tipType === 'ceramic') {
        // Glazed white ceramic - opaque (unlike glass) but just as glossy,
        // via a clearcoat layer on top of a near-white base instead of
        // glass's transmission (ceramic doesn't let light through).
        return new THREE.MeshPhysicalMaterial({
          color: 0xf7f4ee,
          metalness: 0.05,
          roughness: 0.1,
          clearcoat: 1,
          clearcoatRoughness: 0.06,
          side: THREE.DoubleSide
        });
      }
      return new THREE.MeshStandardMaterial({ map: makePaperTexture(hex), bumpMap: makePaperBumpTexture(), bumpScale: 0.008, roughness: 0.72, metalness: 0.01, transparent: true, opacity: 0.93, side: THREE.FrontSide });
    }
    // Jar body/cap material by real finish name (from filters.jar_color_
    // combos on the product) - Clear gets an actual transmissive glass/
    // plastic look, Bamboo reuses the wood-grain texture already built for
    // wood filter tips, Matte/Glossy get the same flat color at different
    // roughness so they visibly read as different finishes under light.
    function makeJarMaterial(colorName) {
      var hex = JAR_FINISH_HEX[colorName] || '#e8e8e8';
      if (colorName === 'Clear') {
        return new THREE.MeshPhysicalMaterial({
          color: 0xffffff,
          metalness: 0,
          roughness: 0.08,
          transmission: 0.85,
          thickness: 0.4,
          ior: 1.45,
          clearcoat: 0.5,
          clearcoatRoughness: 0.1,
          transparent: true,
          opacity: 1,
          side: THREE.DoubleSide
        });
      }
      if (colorName === 'Bamboo') {
        return new THREE.MeshStandardMaterial({ map: makeWoodTexture(), roughness: 0.75, metalness: 0.02, side: THREE.DoubleSide });
      }
      var glossy = colorName.indexOf('Glossy') === 0;
      return new THREE.MeshStandardMaterial({
        color: hex,
        roughness: glossy ? 0.18 : 0.75,
        metalness: glossy ? 0.05 : 0.01,
        side: THREE.DoubleSide
      });
    }
    function makeLogoCanvas() {
      var c = document.createElement('canvas');
      c.width = 1024; c.height = 1024;
      return c;
    }
    function paperDisplayColor(hex) {
      var m = /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex || '');
      if (!m) return hex;
      var r = parseInt(m[1], 16), g = parseInt(m[2], 16), b = parseInt(m[3], 16);
      var lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
      if (lum < 0.86) return hex;
      var t = Math.min((lum - 0.86) / 0.14, 1);
      var factor = 1 - t * 0.1;
      var dr = Math.round(r * factor), dg = Math.round(g * factor), db = Math.round(b * factor);
      return '#' + [dr, dg, db].map(function (v) { return v.toString(16).padStart(2, '0'); }).join('');
    }
    function darkenColor(hex, amount) {
      var m = /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex || '');
      if (!m) return hex;
      var r = parseInt(m[1], 16), g = parseInt(m[2], 16), b = parseInt(m[3], 16);
      var factor = 1 - amount;
      var dr = Math.round(r * factor), dg = Math.round(g * factor), db = Math.round(b * factor);
      return '#' + [dr, dg, db].map(function (v) { return v.toString(16).padStart(2, '0'); }).join('');
    }
    function addRimCap(radius, y, hex) {
      var wallT = Math.max(radius * 0.12, 0.02);
      var ringGeo = new THREE.RingGeometry(Math.max(radius - wallT, 0.001), radius, 48);
      var ringMat = new THREE.MeshStandardMaterial({ color: hex, roughness: 0.5, metalness: 0.05, side: THREE.DoubleSide });
      var ring = new THREE.Mesh(ringGeo, ringMat);
      ring.rotation.x = -Math.PI / 2;
      ring.position.y = y;
      group.add(ring);
    }
    function addSeamRing(radius, y) {
      var torusGeo = new THREE.TorusGeometry(radius, radius * 0.025, 8, 48);
      var torusMat = new THREE.MeshStandardMaterial({ color: 0x000000, roughness: 0.6, metalness: 0.1, transparent: true, opacity: 0.35 });
      var torus = new THREE.Mesh(torusGeo, torusMat);
      torus.rotation.x = Math.PI / 2;
      torus.position.y = y;
      group.add(torus);
    }
    // Real wood pre-roll tips are a solid dowel plug with a drilled airhole
    // straight down the middle, visible as a dark circle on the exposed end
    // - not an open tube like every other tip type. This closes the tube's
    // bottom with an actual wood end-grain cap (rings + a shaded center
    // hole) instead of leaving it hollow.
    function addWoodEndCap(radius, y) {
      var c = document.createElement('canvas');
      c.width = 256; c.height = 256;
      var ctx = c.getContext('2d');
      ctx.fillStyle = '#ddc49c';
      ctx.fillRect(0, 0, 256, 256);
      ctx.strokeStyle = '#a9855a';
      ctx.globalAlpha = 0.4;
      for (var i = 0; i < 5; i++) {
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.arc(128, 128, 18 + i * 20, 0, Math.PI * 2);
        ctx.stroke();
      }
      ctx.globalAlpha = 1;
      var holeR = 30;
      var grad = ctx.createRadialGradient(128, 128, 0, 128, 128, holeR);
      grad.addColorStop(0, '#0c0906');
      grad.addColorStop(0.75, '#241a10');
      grad.addColorStop(1, '#a9855a');
      ctx.fillStyle = grad;
      ctx.beginPath();
      ctx.arc(128, 128, holeR, 0, Math.PI * 2);
      ctx.fill();

      var tex = new THREE.CanvasTexture(c);
      tex.needsUpdate = true;
      var capGeo = new THREE.CircleGeometry(radius, 48);
      var capMat = new THREE.MeshStandardMaterial({ map: sharpenTexture(tex), roughness: 0.75, metalness: 0.01, side: THREE.DoubleSide });
      var cap = new THREE.Mesh(capGeo, capMat);
      cap.rotation.x = Math.PI / 2;
      cap.position.y = y;
      group.add(cap);
    }
    function paintBase(ctx, canvasEl, hex, withGrain) {
      ctx.clearRect(0, 0, canvasEl.width, canvasEl.height);
      ctx.fillStyle = hex;
      ctx.fillRect(0, 0, canvasEl.width, canvasEl.height);
      if (withGrain) {
        paintPaperGrain(ctx, canvasEl.width, '#000000', '#ffffff', 0.015, 0.025);
      }
    }

    var CONE_TIP_SCALE = 0.85, CONE_TIP_MAX = 0.42;
    var TUBE_TIP_SCALE = 0.6, TUBE_TIP_MAX = 0.22;

    var logoCanvas = null, logoCtx = null, logoMesh = null, currentImage = null, currentOrientation = 'horizontal';
    // The logo canvas is always a 1024x1024 SQUARE, but it gets UV-wrapped
    // onto a cylindrical surface (tube band / cone tip) whose real-world
    // circumference is usually much wider than it is tall. Drawing the logo
    // at its true pixel aspect ratio on that square canvas, with no
    // correction, makes it come out stretched horizontally once wrapped -
    // this factor (real surface height / real surface circumference) is
    // applied to the logo's drawn width in renderLogo() to counteract that,
    // set once per buildModel() call to whichever surface is active.
    var logoAspectCorrection = 1;

    function clearGroup() {
      while (group.children.length) {
        var child = group.children.pop();
        if (child.geometry) child.geometry.dispose();
        if (child.material && child.material.map) child.material.map.dispose();
        if (child.material) child.material.dispose();
      }
      logoMesh = null;
    }

    function buildModel() {
      clearGroup();
      var opt = state.option;
      if (!opt) return;
      var swatchHex = (state.shape === 'tube' ? data.colorsTube : data.colorsCone)[state.color] || '#cbb994';
      var displaySwatch = paperDisplayColor(swatchHex);
      var tipDisplaySwatch = darkenColor(displaySwatch, 0.14);
      var bandBaseHex = state.bandColor === 'white' ? '#f0f0f0' : '#141414';

      var lengthNum = parseFloat(opt.length) || 1;
      var filterLenNum = parseFloat(opt.filter_length) || 0;
      // Tubes only: keep the tip/band SIZE identical across Glass/Wood/
      // Spiral at the same size - tip type should only change what the
      // tip is made of, not how big it looks. Real filter_length does
      // vary a little by tip type/diameter in the catalog, so always
      // size off the Glass variant's real filter_length when one exists
      // for this length, falling back to the selected option's own value
      // only when no Glass reference exists at this size.
      if (state.shape === 'tube') {
        var glassRef = optionsForShape('tube').filter(function (o) {
          return o.length === opt.length && o.tip_type === 'glass' && o.filter_length;
        })[0];
        if (glassRef) filterLenNum = parseFloat(glassRef.filter_length) || filterLenNum;
      }
      var seamFrac = filterLenNum / lengthNum;

      var TOP_R = state.shape === 'cone' ? 0.39 : 0.3;
      var TIP_R = state.shape === 'cone' ? TOP_R * 0.42 : TOP_R;

      if (state.shape === 'cone') {
        var displaySeam = Math.min(seamFrac * CONE_TIP_SCALE, CONE_TIP_MAX);
        var tipH = TOTAL_H * displaySeam;
        var bodyH = TOTAL_H - tipH;
        var midR = TIP_R + (TOP_R - TIP_R) * displaySeam;

        var bodyGeo = new THREE.CylinderGeometry(TOP_R, midR, bodyH, 48, 1, true);
        var bodyMat = new THREE.MeshStandardMaterial({ map: makePaperTexture(displaySwatch), bumpMap: makePaperBumpTexture(), bumpScale: 0.008, roughness: 0.72, metalness: 0.01, transparent: true, opacity: 0.93, side: THREE.FrontSide });
        var bodyMesh = new THREE.Mesh(bodyGeo, bodyMat);
        bodyMesh.position.y = TOTAL_H / 2 - bodyH / 2;
        group.add(bodyMesh);
        addRimCap(TOP_R, TOTAL_H / 2, displaySwatch);

        logoCanvas = makeLogoCanvas();
        logoCtx = logoCanvas.getContext('2d');
        paintBase(logoCtx, logoCanvas, tipDisplaySwatch, true);
        var tipCircumference = 2 * Math.PI * ((midR + TIP_R) / 2);
        logoAspectCorrection = tipH / tipCircumference;
        var tipTex = faceFrontTexture(sharpenTexture(new THREE.CanvasTexture(logoCanvas)));
        var tipGeo = new THREE.CylinderGeometry(midR, TIP_R, tipH, 48, 1, true);
        var tipMat = new THREE.MeshStandardMaterial({ map: tipTex, roughness: 0.65, metalness: 0.02, transparent: true, opacity: 0.93, side: THREE.FrontSide });
        logoMesh = new THREE.Mesh(tipGeo, tipMat);
        logoMesh.position.y = TOTAL_H / 2 - bodyH - tipH / 2;
        group.add(logoMesh);
        addSeamRing(midR, TOTAL_H / 2 - bodyH);
      } else if (state.shape === 'jar') {
        var combo = state.jarCombo || { jar: 'Clear', cap: 'Matte Black' };
        var diameterMM = parseFloat(opt.diameter) || 60;
        var heightMM = parseFloat(opt.length) || 0;

        // Jars are wide/squat (real height/diameter ratios in this catalog
        // run ~0.3-0.6 - shorter than they are wide), unlike cone/tube
        // where the tall vertical dimension is the hero measurement -  so
        // DIAMETER, not height, is scaled to be the dominant on-screen
        // size here. Real diameters run ~34mm (small concentrate jars) to
        // ~120mm (large flower jars), mapped across a wide-but-bounded
        // range so the smallest and largest real jars both read clearly
        // and distinctly in frame.
        var jarR = 1.0 + Math.max(0, Math.min(1, (diameterMM - 34) / 86)) * 1.6;
        // Real jars (see reference photos) are straight-sided with a flat
        // wide lid nearly the same width as the body, not a narrow-necked
        // bottle - the lid sits ON the body's own diameter, just a touch
        // wider so it visibly overlaps the rim.
        var capR = jarR * 1.08;
        var capH = TOTAL_H * 0.13;
        // Height isn't in every product's title (5 of the 10 real jars
        // only give capacity, not a height) - when it's missing, fall
        // back to the real catalog's typical ratio (~0.3-0.6) instead of
        // guessing a specific fake number. Applied directly with no extra
        // boost/scale factor, since scaling height alone (independent of
        // width) would silently distort the real aspect ratio right back
        // out of proportion.
        var aspect = (heightMM && diameterMM) ? Math.max(0.28, Math.min(1.3, heightMM / diameterMM)) : 0.5;
        var bodyH = jarR * 2 * aspect;
        var jarBottomY = -TOTAL_H / 2;

        // Straight-walled cylinder, closed top and bottom (the top is
        // hidden under the cap mesh anyway, the bottom needs to look like
        // a real solid base rather than an open tube).
        var bodyGeo = new THREE.CylinderGeometry(jarR, jarR, bodyH, 48, 1, false);
        var bodyMat = makeJarMaterial(combo.jar);
        var bodyMesh = new THREE.Mesh(bodyGeo, bodyMat);
        bodyMesh.position.y = jarBottomY + bodyH / 2;
        group.add(bodyMesh);

        var capGeo = new THREE.CylinderGeometry(capR, capR, capH, 48, 1, false);
        var capMat = makeJarMaterial(combo.cap);
        var capMesh = new THREE.Mesh(capGeo, capMat);
        capMesh.position.y = jarBottomY + bodyH + capH / 2;
        group.add(capMesh);

        // Logo/branding goes on its own thin cylindrical "label" band
        // around the body's straight wall - a separate mesh keeps clean
        // rectangular UV wrapping regardless of body proportions.
        var labelH = bodyH * 0.42;
        var labelY = jarBottomY + bodyH * 0.5;
        var labelR = jarR + 0.003;

        logoCanvas = makeLogoCanvas();
        logoCtx = logoCanvas.getContext('2d');
        paintBase(logoCtx, logoCanvas, '#ffffff', false);
        var labelCircumference = 2 * Math.PI * labelR;
        logoAspectCorrection = labelH / labelCircumference;
        var labelTex = faceFrontTexture(sharpenTexture(new THREE.CanvasTexture(logoCanvas)));
        var labelGeo = new THREE.CylinderGeometry(labelR, labelR, labelH, 48, 1, true);
        // No transparent:true here (unlike the cone tip material) - the
        // label canvas is always fully opaque (solid base fill, even with
        // no logo yet), and flagging it transparent anyway was producing
        // a visible seam line at the UV wrap boundary under some camera
        // angles, same as the tube band material already correctly avoids.
        var labelMat = new THREE.MeshStandardMaterial({ map: labelTex, roughness: 0.4, metalness: 0.05, side: THREE.DoubleSide });
        logoMesh = new THREE.Mesh(labelGeo, labelMat);
        logoMesh.position.y = labelY;
        group.add(logoMesh);
      } else {
        var bandH = TOTAL_H * 0.16;
        var displaySeamTube = Math.min(seamFrac * TUBE_TIP_SCALE, TUBE_TIP_MAX);
        var seamY = TOTAL_H * displaySeamTube - TOTAL_H / 2;
        var bandTopY = seamY + bandH / 2;
        var topH = TOTAL_H / 2 - bandTopY;
        var bottomH = TOTAL_H - topH - bandH;

        var topGeo = new THREE.CylinderGeometry(TOP_R, TOP_R, Math.max(topH, 0.001), 48, 1, true);
        var topMat = new THREE.MeshStandardMaterial({ map: makePaperTexture(displaySwatch), bumpMap: makePaperBumpTexture(), bumpScale: 0.008, roughness: 0.72, metalness: 0.01, transparent: true, opacity: 0.93, side: THREE.FrontSide });
        var topMesh = new THREE.Mesh(topGeo, topMat);
        topMesh.position.y = TOTAL_H / 2 - topH / 2;
        group.add(topMesh);
        addRimCap(TOP_R, TOTAL_H / 2, displaySwatch);

        logoCanvas = makeLogoCanvas();
        logoCtx = logoCanvas.getContext('2d');
        paintBase(logoCtx, logoCanvas, bandBaseHex);
        var bandCircumference = 2 * Math.PI * TOP_R;
        logoAspectCorrection = bandH / bandCircumference;
        var bandTex = faceFrontTexture(sharpenTexture(new THREE.CanvasTexture(logoCanvas)));
        var bandGeo = new THREE.CylinderGeometry(TOP_R, TOP_R, bandH, 48, 1, true);
        var bandMat = new THREE.MeshStandardMaterial({ map: bandTex, roughness: 0.3, metalness: 0.15, side: THREE.DoubleSide });
        logoMesh = new THREE.Mesh(bandGeo, bandMat);
        logoMesh.position.y = bandTopY - bandH / 2;
        group.add(logoMesh);
        addSeamRing(TOP_R, bandTopY);
        addSeamRing(TOP_R, bandTopY - bandH);

        // The tip MATERIAL must follow what the shopper actually selected
        // (state.tipType), not opt.tip_type - opt is only a price/geometry
        // reference and, for tip types with no real SKUs yet (Ceramic),
        // that reference product is borrowed from a different tip type
        // (Glass), so opt.tip_type would render the wrong material.
        var resolvedTipType = state.tipType || opt.tip_type;
        var bottomGeo = new THREE.CylinderGeometry(TOP_R, TOP_R, Math.max(bottomH, 0.001), 48, 1, true);
        var bottomMat = makeTipMaterial(tipDisplaySwatch, resolvedTipType);
        var bottomMesh = new THREE.Mesh(bottomGeo, bottomMat);
        bottomMesh.position.y = -TOTAL_H / 2 + bottomH / 2;
        if (resolvedTipType === 'wood') {
          addWoodEndCap(TOP_R, -TOTAL_H / 2);
        }
        group.add(bottomMesh);
      }

      group.traverse(function (o) { if (o.isMesh) o.castShadow = true; });

      group.rotation.set(0, 0, 0);
      camera.position.z = DEFAULT_Z;

      if (currentImage) renderLogo();

      if (badge) {
        badge.hidden = false;
        // Paper type belongs to whichever real product is being used as
        // the pricing reference, which no longer necessarily matches the
        // selected color (color is picked from the full palette
        // independently) - showing it here would risk a mismatched-
        // looking pairing, so the badge is just the color name now. Jars
        // have no single "color" (it's a Jar+Cap pair), so show that pair
        // instead.
        if (badgeName) {
          badgeName.textContent = state.shape === 'jar'
            ? (state.jarCombo ? state.jarCombo.jar + ' + ' + state.jarCombo.cap : '')
            : state.color;
        }
        if (badgeSub) badgeSub.textContent = '';
        badge.classList.add('cone-studio__badge--single');
      }
    }

    // ---- logo drawing (same fill-by-height logic as the product page) ----
    function renderLogo() {
      if (!logoCtx || !currentImage) return;
      var W = logoCanvas.width, H = logoCanvas.height;
      var swatchHex = (state.shape === 'tube' ? data.colorsTube : data.colorsCone)[state.color] || '#cbb994';
      var displaySwatch = paperDisplayColor(swatchHex);
      var tipDisplaySwatch = darkenColor(displaySwatch, 0.14);
      // Jars: the label band is always a plain white sticker background
      // (like a real printed label), regardless of the jar/cap finish
      // underneath it.
      var baseHex = state.shape === 'jar' ? '#ffffff' : state.shape === 'tube' ? (state.bandColor === 'white' ? '#f0f0f0' : '#141414') : tipDisplaySwatch;
      logoCtx.clearRect(0, 0, W, H);
      logoCtx.fillStyle = baseHex;
      logoCtx.fillRect(0, 0, W, H);

      var scalePct = sizeInput ? parseFloat(sizeInput.value) : 100;
      var srcW = currentImage.naturalWidth || currentImage.width;
      var srcH = currentImage.naturalHeight || currentImage.height;
      var rotate = currentOrientation === 'vertical';
      var effW = rotate ? srcH : srcW;
      var effH = rotate ? srcW : srcH;
      var scale = (H / effH) * (scalePct / 100);
      // drawW always ends up spanning the canvas's horizontal (circumference)
      // axis in the final orientation - even when rotated, since effW/effH
      // are already swapped for that case above - so this is the one place
      // that needs the aspect correction (see logoAspectCorrection comment).
      var drawW = effW * scale * (logoAspectCorrection || 1), drawH = effH * scale;

      logoCtx.save();
      logoCtx.translate(W / 2, H / 2);
      if (rotate) {
        logoCtx.rotate(-Math.PI / 2);
        logoCtx.drawImage(currentImage, -drawH / 2, -drawW / 2, drawH, drawW);
      } else {
        logoCtx.drawImage(currentImage, -drawW / 2, -drawH / 2, drawW, drawH);
      }
      logoCtx.restore();
      if (logoMesh) logoMesh.material.map.needsUpdate = true;
    }

    // ---- Type / Size / Filter Tip / Color pickers ----
    function renderSizeRow() {
      sizeRow.innerHTML = '';
      // Jars: no shared "length" to group by (a Glass 3oz jar and a
      // Plastic 116mm jar have nothing dimensionally in common), so each
      // real product is its own tile instead of a grouped size list.
      if (state.shape === 'jar') {
        optionsForShape('jar').forEach(function (o) {
          var btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'cone-studio__pill-btn';
          btn.textContent = jarSizeLabel(o);
          if (state.option && o.handle === state.option.handle) btn.classList.add('is-active');
          btn.addEventListener('click', function () {
            state.option = o;
            state.jarCombo = parseCombos(o.combos)[0] || null;
            renderSizeRow();
            renderColorGrid();
            buildModel();
          });
          sizeRow.appendChild(btn);
        });
        return;
      }
      lengthsForShape(state.shape).forEach(function (len) {
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'cone-studio__pill-btn';
        btn.textContent = (state.shape === 'cone' ? 'Cone ' : 'Tube ') + len;
        btn.dataset.length = len;
        if (len === state.length) btn.classList.add('is-active');
        btn.addEventListener('click', function () {
          state.length = len;
          var tipTypes = tipTypesForLength(state.shape, len);
          state.tipType = tipTypes[0] || null;
          state.option = referenceOptionFor(state.shape, len, state.tipType);
          // Color is independent of size now - a shopper picking a
          // different length shouldn't lose (or have reshuffled) their
          // color choice, so it's left as-is here.
          if (!state.color) state.color = fullPaletteColors(state.shape)[0] || null;
          renderSizeRow();
          renderTipRow();
          renderColorGrid();
          syncBandSection();
          syncTipSection();
          buildModel();
        });
        sizeRow.appendChild(btn);
      });
    }
    function renderTipRow() {
      if (!tipRow) return;
      tipRow.innerHTML = '';
      tipTypesForLength(state.shape, state.length).forEach(function (tt) {
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'cone-studio__pill-btn';
        btn.textContent = TIP_TYPE_LABELS[tt] || tt;
        if (tt === state.tipType) btn.classList.add('is-active');
        btn.addEventListener('click', function () {
          state.tipType = tt;
          state.option = referenceOptionFor(state.shape, state.length, tt);
          // Color list no longer depends on tip type (it's the fixed
          // palette), so switching Glass/Wood/Spiral keeps whatever color
          // was already selected instead of resetting or reshuffling it.
          renderTipRow();
          renderColorGrid();
          buildModel();
        });
        tipRow.appendChild(btn);
      });
    }
    function renderColorGrid() {
      colorGrid.innerHTML = '';
      if (colorTitleEl) colorTitleEl.textContent = state.shape === 'jar' ? 'Jar & Cap Color' : 'Paper Color';
      // Jars: no shared global palette - only the combos the CURRENT real
      // product's own description actually lists are shown, as a split
      // two-tone swatch (left = jar color, right = cap color) so an
      // invalid pairing can never be picked.
      if (state.shape === 'jar') {
        if (!state.option) return;
        parseCombos(state.option.combos).forEach(function (combo) {
          var card = document.createElement('button');
          card.type = 'button';
          card.className = 'cone-studio__color-circle';
          var isActive = state.jarCombo && state.jarCombo.jar === combo.jar && state.jarCombo.cap === combo.cap;
          if (isActive) card.classList.add('is-active');
          var jarHex = JAR_FINISH_HEX[combo.jar] || '#e8e8e8';
          var capHex = JAR_FINISH_HEX[combo.cap] || '#e8e8e8';
          card.style.background = 'linear-gradient(90deg, ' + jarHex + ' 50%, ' + capHex + ' 50%)';
          var label = combo.jar + ' Jar + ' + combo.cap + ' Cap';
          card.dataset.tooltip = label;
          card.setAttribute('aria-label', label);
          card.addEventListener('click', function () {
            state.jarCombo = combo;
            renderColorGrid();
            buildModel();
          });
          colorGrid.appendChild(card);
        });
        return;
      }
      var palette = state.shape === 'tube' ? data.colorsTube : data.colorsCone;
      fullPaletteColors(state.shape).forEach(function (colorName) {
        var card = document.createElement('button');
        card.type = 'button';
        card.className = 'cone-studio__color-circle';
        if (colorName === state.color) card.classList.add('is-active');
        var hex = palette[colorName] || '#cbb994';
        card.style.backgroundColor = hex;
        card.dataset.tooltip = colorName;
        card.setAttribute('aria-label', colorName);
        card.addEventListener('click', function () {
          // state.option (the pricing/geometry reference) is untouched -
          // color is purely a swatch choice now, not tied to a specific
          // real product.
          state.color = colorName;
          renderColorGrid();
          buildModel();
        });
        colorGrid.appendChild(card);
      });
    }
    function syncBandSection() {
      bandSection.hidden = state.shape !== 'tube';
    }
    function syncTipSection() {
      if (!tipSection) return;
      tipSection.hidden = state.shape !== 'tube' || tipTypesForLength(state.shape, state.length).length < 2;
    }
    function selectShape(shape) {
      if (!optionsForShape(shape).length) return;
      state.shape = shape;
      typeRow.querySelectorAll('[data-studio-type]').forEach(function (b) {
        b.classList.toggle('is-active', b.dataset.studioType === shape);
      });
      if (shape === 'jar') {
        state.length = null;
        state.tipType = null;
        state.color = null;
        state.option = optionsForShape('jar')[0] || null;
        state.jarCombo = state.option ? parseCombos(state.option.combos)[0] || null : null;
        renderSizeRow();
        renderTipRow();
        renderColorGrid();
        syncBandSection();
        syncTipSection();
        buildModel();
        return;
      }
      state.jarCombo = null;
      var lens = lengthsForShape(shape);
      state.length = lens[0] || null;
      var tipTypes = state.length ? tipTypesForLength(shape, state.length) : [];
      state.tipType = tipTypes[0] || null;
      state.option = state.length ? referenceOptionFor(shape, state.length, state.tipType) : null;
      // Cone and Tube have independent color lists (see the Shape field on
      // each Metaobject entry) - reset to that shape's first color rather
      // than carrying over a name that might not exist in its palette.
      state.color = fullPaletteColors(shape)[0] || null;
      renderSizeRow();
      renderTipRow();
      renderColorGrid();
      syncBandSection();
      syncTipSection();
      buildModel();
    }
    typeRow.querySelectorAll('[data-studio-type]').forEach(function (btn) {
      btn.addEventListener('click', function () { selectShape(btn.dataset.studioType); });
    });

    // ---- band color ----
    root.querySelectorAll('[data-studio-band-choice]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        state.bandColor = btn.dataset.studioBandChoice;
        root.querySelectorAll('[data-studio-band-choice]').forEach(function (b) {
          b.classList.toggle('is-active', b === btn);
        });
        if (currentImage) { renderLogo(); } else { buildModel(); }
      });
    });

    // ---- background ----
    bgRow.querySelectorAll('[data-studio-bg]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        state.background = btn.dataset.studioBg;
        applyBackground(state.background);
        bgRow.querySelectorAll('[data-studio-bg]').forEach(function (b) {
          b.classList.toggle('is-active', b === btn);
        });
      });
    });

    // ---- logo upload controls ----
    var fileInput = root.querySelector('[data-studio-logo-file]');
    var uploadLabel = root.querySelector('[data-studio-upload-label]');
    var logoPreview = root.querySelector('[data-studio-logo-preview]');
    var logoPreviewImg = root.querySelector('[data-studio-logo-preview-img]');
    var logoPreviewRemove = root.querySelector('[data-studio-logo-remove]');
    var orientationRow = root.querySelector('[data-studio-logo-orientation-row]');
    var toolbar = root.querySelector('[data-studio-logo-toolbar]');
    var sizeInput = root.querySelector('[data-studio-logo-size]');

    // Single-step now: as soon as a logo is uploaded it shows on the model
    // AND as a thumbnail in the upload box (with its own remove button)
    // immediately, alongside orientation/size controls - no more
    // upload -> orientation -> toolbar wizard steps to click through.
    function syncLogoUI() {
      var has = !!currentImage;
      if (uploadLabel) uploadLabel.hidden = has;
      if (logoPreview) logoPreview.hidden = !has;
      if (orientationRow) orientationRow.hidden = !has;
      if (toolbar) toolbar.hidden = !has;
      if (has && logoPreviewImg) logoPreviewImg.src = currentImage.src;
    }
    fileInput.addEventListener('change', function () {
      var file = fileInput.files && fileInput.files[0];
      if (!file || file.type.indexOf('image/') !== 0) return;
      var reader = new FileReader();
      reader.onload = function (e) {
        var img = new Image();
        img.onload = function () {
          currentImage = img;
          renderLogo();
          syncLogoUI();
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    });
    orientationRow.querySelectorAll('[data-studio-orientation]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        currentOrientation = btn.dataset.studioOrientation;
        orientationRow.querySelectorAll('[data-studio-orientation]').forEach(function (b) {
          b.classList.toggle('is-active', b === btn);
        });
        sizeInput.value = 100;
        renderLogo();
      });
    });
    sizeInput.addEventListener('input', renderLogo);
    if (logoPreviewRemove) logoPreviewRemove.addEventListener('click', function () {
      currentImage = null;
      fileInput.value = '';
      buildModel();
      syncLogoUI();
    });

    // ---- quantity / pricing ----
    // Tiers are discount PERCENTAGES (same tiers as the Bulk Pricing
    // table on product pages), applied to the currently selected real
    // product's own real price - so pricing already varies correctly by
    // size and color, since those are different real products with
    // different real prices, without needing a separate price table per
    // combination.
    // Cone and Tube each have their own tier list (see the two textareas
    // in the section schema) - real bulk pricing structure can genuinely
    // differ between them, same reason colors got split by shape earlier.
    function parseTiers(raw) {
      var out = [];
      (raw || '').split('\n').forEach(function (line) {
        var parts = line.split(',');
        if (parts.length === 3) {
          var min = parseInt(parts[0], 10);
          var max = parts[1] === '' ? Infinity : parseInt(parts[1], 10);
          var percent = parseFloat(parts[2]) || 0;
          if (min) out.push({ min: min, max: max, percent: percent });
        }
      });
      return out;
    }
    var discountTiersCone = parseTiers(data.bulkTiersCone);
    var discountTiersTube = parseTiers(data.bulkTiersTube);
    var discountTiersJar = parseTiers(data.bulkTiersJar);
    function tiersForShape(shape) {
      if (shape === 'tube') return discountTiersTube;
      if (shape === 'jar') return discountTiersJar;
      return discountTiersCone;
    }
    function percentForQty(qty) {
      var tiers = tiersForShape(state.shape);
      for (var i = 0; i < tiers.length; i++) {
        if (qty >= tiers[i].min && qty <= tiers[i].max) return tiers[i].percent;
      }
      return 0;
    }
    function updateQuoteTotal() {
      if (!state.qty || !state.option) {
        quoteTotalEl.textContent = 'Select a quantity';
        if (quotePerUnitEl) quotePerUnitEl.textContent = '—';
        return;
      }
      var percent = percentForQty(state.qty);
      // state.option.price is the real product's real BOX price (same
      // field the product page's own Bulk Pricing table starts from), and
      // the quantity tiers here quote in that many boxes - so this is the
      // discounted price per BOX, and (unitPrice * qty) below is the real
      // total cost for that many boxes.
      var unitPrice = state.option.price * (1 - percent / 100);
      state.pricePerUnit = unitPrice;
      quoteTotalEl.textContent = '$' + (unitPrice * state.qty).toFixed(2) + (percent ? ' (' + percent + '% off)' : '');

      // True per-cone price needs dividing by how many cones are actually
      // in that box (filters.case_quantity on the real product) - the
      // exact same math the product-page Bulk Pricing table itself does
      // (bulk-pricing-table.liquid), including its 3-decimal precision
      // (a plain 2-decimal cent price collapses most tiers to the same
      // 1-2 cent figure once divided across a real bulk case). Products
      // missing that metafield just don't show a per-cone figure rather
      // than showing a wrong one.
      if (quotePerUnitEl) {
        var caseQty = state.option.caseQty;
        if (caseQty && caseQty > 0) {
          quotePerUnitEl.textContent = '$' + (unitPrice / caseQty).toFixed(3) + ' / cone';
        } else {
          quotePerUnitEl.textContent = '—';
        }
      }
    }
    // Quantity buttons are generated straight from the real tier ranges
    // (the same ranges shown in the product-page Bulk Pricing table) -
    // not a separate, disconnected set of round numbers. Clicking a
    // button quotes at that tier's minimum quantity, e.g. the "96+" tier
    // quotes 96 units at that tier's discount.
    function renderQuantityGrid() {
      qtyGrid.innerHTML = '';
      tiersForShape(state.shape).forEach(function (tier) {
        var label = tier.max === Infinity ? (tier.min + '+') : (tier.min + '-' + tier.max);
        var card = document.createElement('button');
        card.type = 'button';
        card.className = 'cone-studio__qty-card';
        if (tier.min === state.qty) card.classList.add('is-active');
        card.innerHTML = '<div class="cone-studio__qty-number">' + label + '</div><div class="cone-studio__qty-unit">units' + (tier.percent ? ' · ' + tier.percent + '% off' : '') + '</div>';
        card.addEventListener('click', function () {
          state.qty = tier.min;
          qtyGrid.querySelectorAll('.cone-studio__qty-card').forEach(function (c) { c.classList.remove('is-active'); });
          card.classList.add('is-active');
          updateQuoteTotal();
          refreshQuoteBody();
          if (quoteSubmitBtn) quoteSubmitBtn.disabled = false;
        });
        qtyGrid.appendChild(card);
      });
    }

    // ---- export ----
    // Computed the same way as DEFAULT_Z. The margin here used to be only
    // 2%, which left almost no breathing room top or bottom - the model
    // read as "cut off" against the frame edges in the exported/quote
    // preview image even though the padding was already symmetric (this
    // camera setup is always centered, so top and bottom margins are
    // mathematically equal by construction - the issue was just that both
    // were too small). A more generous, still-tight margin keeps the
    // model large while giving it real, equal space top and bottom.
    var EXPORT_Z = (TOTAL_H / 2 * 1.32) / Math.tan(halfFovRad);

    // Same tiled-logo watermark technique as the product-page size chart
    // (assets/product-size-chart-3d.js) - real site logo tiled diagonally
    // at low opacity, falling back to repeated brand text if the logo
    // element isn't found for some reason.
    function loadImagePromise(src, allowCrossOrigin) {
      return new Promise(function (resolve) {
        var img = new Image();
        if (allowCrossOrigin) img.crossOrigin = 'anonymous';
        img.onload = function () { resolve(img); };
        img.onerror = function () { resolve(null); };
        img.src = src;
      });
    }
    function tileWatermark(ctx, w, h, watermarkImg) {
      ctx.save();
      ctx.translate(w / 2, h / 2);
      ctx.rotate(-Math.PI / 8);
      ctx.translate(-w, -h);
      var spanX = w * 3, spanY = h * 3;
      if (watermarkImg) {
        ctx.globalAlpha = 0.1;
        var tileW = w * 0.14;
        var tileH = tileW * (watermarkImg.naturalHeight / watermarkImg.naturalWidth || 0.4);
        var stepX = tileW + w * 0.07, stepY = tileH + w * 0.07;
        for (var y = 0; y < spanY; y += stepY) {
          for (var x = 0; x < spanX; x += stepX) ctx.drawImage(watermarkImg, x, y, tileW, tileH);
        }
      } else {
        ctx.globalAlpha = 0.1;
        ctx.fillStyle = '#ffffff';
        ctx.font = '700 ' + Math.round(w * 0.028) + 'px Arial, Helvetica, sans-serif';
        ctx.textAlign = 'center';
        for (var yt = 0; yt < spanY; yt += w * 0.09) {
          for (var xt = 0; xt < spanX; xt += w * 0.24) ctx.fillText('KING CUSTOM CONES', xt, yt);
        }
      }
      ctx.restore();
    }
    var EXPORT_SIZE = 2000;
    function exportImage() {
      // The live canvas is only ever as big as its on-screen CSS size (x
      // the device pixel ratio) - fine for the interactive view, but a
      // "download my design" image needs to stand on its own at a real
      // fixed resolution regardless of what size window/device it was
      // designed on. Render one frame at a proper flat 2000x2000 instead
      // of just copying whatever the live canvas happens to be, then put
      // the live renderer size back exactly as it was.
      var liveZ = camera.position.z;
      renderer.setSize(EXPORT_SIZE, EXPORT_SIZE, false);
      camera.aspect = 1;
      camera.position.z = EXPORT_Z;
      camera.updateProjectionMatrix();
      renderer.render(scene, camera);
      var glCanvas = renderer.domElement;

      // This page runs on its own stripped-down layout with no site
      // header at all (a deliberate full-screen builder), so the header-
      // logo lookup the product-page export uses would never find
      // anything here - the real store logo (this section's own
      // "Watermark logo" setting) comes straight from the page's own data
      // instead. The header selector stays as a fallback in case a future
      // layout change adds a header back to this page.
      var logoImgEl = document.querySelector('.header__heading-logo img, a.header__heading-logo-link img, header img[src*="logo" i]');
      var watermarkSrc = data.logoUrl || (logoImgEl ? logoImgEl.currentSrc || logoImgEl.src : null);

      return (watermarkSrc ? loadImagePromise(watermarkSrc, true) : Promise.resolve(null)).then(function (watermarkImg) {
        var outCanvas = document.createElement('canvas');
        outCanvas.width = EXPORT_SIZE;
        outCanvas.height = EXPORT_SIZE;
        var ctx = outCanvas.getContext('2d');
        // The 3D render already carries the shopper's chosen background
        // color (scene.background) baked in, so this is just the model on
        // its real background, then the watermark tiled on top - the
        // margin the camera framing leaves around the model is where it
        // reads most clearly, same as the product-page export. JPEG (not
        // PNG) needs an explicitly opaque base first since it has no
        // alpha channel of its own to fall back on.
        ctx.fillStyle = state.background === 'white' ? '#ffffff' : state.background === 'warm' ? '#e8e4dd' : '#2b2e30';
        ctx.fillRect(0, 0, EXPORT_SIZE, EXPORT_SIZE);
        ctx.drawImage(glCanvas, 0, 0, EXPORT_SIZE, EXPORT_SIZE);
        var showWatermark = !(window.theme && window.theme.coneBuilderSettings && window.theme.coneBuilderSettings.showWatermark === false);
        if (showWatermark) {
          try { tileWatermark(ctx, outCanvas.width, outCanvas.height, watermarkImg); } catch (e) {}
        }

        var dataUrl = outCanvas.toDataURL('image/jpeg', 0.92);
        camera.position.z = liveZ;
        resize(); // restores renderer size + camera.aspect from the real live canvas CSS size
        renderer.render(scene, camera);
        return dataUrl;
      });
    }
    function timestampedFilename(shape) {
      var d = new Date();
      var pad = function (n) { return String(n).padStart(2, '0'); };
      var stamp = d.getFullYear() + pad(d.getMonth() + 1) + pad(d.getDate()) + '-' + pad(d.getHours()) + pad(d.getMinutes()) + pad(d.getSeconds());
      return shape + '-export-' + stamp + '.jpg';
    }
    exportBtn.addEventListener('click', function () {
      exportImage().then(function (url) {
        var a = document.createElement('a');
        a.href = url;
        a.download = timestampedFilename(state.shape);
        document.body.appendChild(a);
        a.click();
        a.remove();
      });
    });

    // ---- quote modal ----
    var quoteModal = document.querySelector('[data-studio-quote-modal]');
    var quoteClose = document.querySelector('[data-studio-quote-close]');
    var quotePreviewImg = document.querySelector('[data-studio-quote-preview-img]');
    var quoteTotalEl = document.querySelector('[data-studio-quote-total]');
    var quotePerUnitEl = document.querySelector('[data-studio-quote-per-unit]');
    var quoteBodyField = document.querySelector('[data-studio-quote-body]');
    var quoteFileInput = document.querySelector('[data-studio-quote-file]');
    var quoteForm = document.querySelector('.cone-studio__quote-form');
    var quoteSubmitBtn = document.querySelector('.cone-studio__quote-submit');

    function refreshQuoteBody() {
      if (!state.option) return;
      var percent = state.qty ? percentForQty(state.qty) : 0;
      var total = state.qty && state.pricePerUnit ? (state.pricePerUnit * state.qty).toFixed(2) : null;
      var lines = ['Custom cone builder design request', '', 'Shape: ' + state.shape];
      if (state.shape === 'jar') {
        var sizeBits = [state.option.material, state.option.diameter];
        if (state.option.capacity) sizeBits.push(state.option.capacity);
        else if (state.option.length) sizeBits.push(state.option.length + ' height');
        lines.push('Size: ' + sizeBits.join(' / '));
        if (state.jarCombo) lines.push('Color: ' + state.jarCombo.jar + ' Jar + ' + state.jarCombo.cap + ' Cap');
      } else {
        lines.push('Size: ' + state.length);
        lines.push('Color: ' + state.color);
        if (state.shape === 'tube') lines.push('Band color: ' + state.bandColor);
      }
      lines.push('Reference product (pricing only): ' + state.option.handle);
      lines.push('Base unit price: $' + state.option.price.toFixed(2));
      lines.push(state.qty ? 'Quantity: ' + state.qty : 'Quantity: (not yet selected)');
      if (percent) lines.push('Discount: ' + percent + '%');
      if (total) lines.push('Estimated total: $' + total);
      lines.push(currentImage ? 'Logo uploaded: yes (attached)' : 'Logo uploaded: no');
      quoteBodyField.value = lines.join('\n');
    }

    ctaBtn.addEventListener('click', function () {
      if (!state.option) return;
      state.qty = null;
      if (quoteSubmitBtn) quoteSubmitBtn.disabled = true;
      renderQuantityGrid();
      updateQuoteTotal();
      refreshQuoteBody();

      // Attach the exported design as a real file on the native contact
      // form's own (hidden) file input via the DataTransfer API, so it
      // goes out as a real email attachment through Shopify's own
      // contact-form handler - no custom backend needed. The customer
      // never sees this as a separate step - it's their own design, not
      // something they need to "choose a file" for themselves.
      exportImage().then(function (dataUrl) {
        quotePreviewImg.src = dataUrl;
        try {
          var byteString = atob(dataUrl.split(',')[1]);
          var arr = new Uint8Array(byteString.length);
          for (var i = 0; i < byteString.length; i++) arr[i] = byteString.charCodeAt(i);
          var blob = new Blob([arr], { type: 'image/jpeg' });
          var file = new File([blob], timestampedFilename(state.shape), { type: 'image/jpeg' });
          var dt = new DataTransfer();
          dt.items.add(file);
          quoteFileInput.files = dt.files;
        } catch (e) {}
      });

      quoteModal.hidden = false;
    });
    quoteClose.addEventListener('click', function () { quoteModal.hidden = true; });

    // ---- drag to rotate / scroll to zoom ----
    var dragging = false, lastX = 0, lastY = 0;
    function pointerDown(e) {
      dragging = true;
      var p = e.touches ? e.touches[0] : e;
      lastX = p.clientX; lastY = p.clientY;
      canvas.style.cursor = 'grabbing';
    }
    function pointerMove(e) {
      if (!dragging) return;
      var p = e.touches ? e.touches[0] : e;
      var dx = p.clientX - lastX, dy = p.clientY - lastY;
      lastX = p.clientX; lastY = p.clientY;
      group.rotation.y += dx * 0.012;
      group.rotation.x = Math.max(-0.5, Math.min(0.5, group.rotation.x + dy * 0.008));
      e.preventDefault();
    }
    function pointerUp() { dragging = false; canvas.style.cursor = 'grab'; }
    canvas.addEventListener('mousedown', pointerDown);
    window.addEventListener('mousemove', pointerMove);
    window.addEventListener('mouseup', pointerUp);
    canvas.addEventListener('touchstart', pointerDown, { passive: true });
    canvas.addEventListener('touchmove', pointerMove, { passive: false });
    canvas.addEventListener('touchend', pointerUp);
    canvas.addEventListener('wheel', function (e) {
      e.preventDefault();
      camera.position.z = Math.max(MIN_Z, Math.min(MAX_Z, camera.position.z + e.deltaY * 0.01));
    }, { passive: false });

    // ---- view controls: a light subset of the reference site's panel -
    // preset angles + zoom step/reset. Drag-to-rotate and scroll-to-zoom
    // already cover free camera control, so this is just quick shortcuts. ----
    var VIEW_PRESETS = {
      front: { y: 0, x: 0 },
      side: { y: Math.PI / 2, x: 0 },
      iso: { y: Math.PI / 4, x: 0.28 }
    };
    document.querySelectorAll('[data-studio-view]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var preset = VIEW_PRESETS[btn.dataset.studioView];
        if (!preset) return;
        group.rotation.y = preset.y;
        group.rotation.x = preset.x;
        document.querySelectorAll('[data-studio-view]').forEach(function (b) { b.classList.remove('is-active'); });
        btn.classList.add('is-active');
      });
    });
    var zoomOutBtn = document.querySelector('[data-studio-zoom-out]');
    var zoomInBtn = document.querySelector('[data-studio-zoom-in]');
    var viewResetBtn = document.querySelector('[data-studio-view-reset]');
    var ZOOM_STEP = (MAX_Z - MIN_Z) * 0.12;
    if (zoomOutBtn) zoomOutBtn.addEventListener('click', function () {
      camera.position.z = Math.min(MAX_Z, camera.position.z + ZOOM_STEP);
    });
    if (zoomInBtn) zoomInBtn.addEventListener('click', function () {
      camera.position.z = Math.max(MIN_Z, camera.position.z - ZOOM_STEP);
    });
    if (viewResetBtn) viewResetBtn.addEventListener('click', function () {
      group.rotation.y = 0;
      group.rotation.x = 0;
      camera.position.z = DEFAULT_Z;
      document.querySelectorAll('[data-studio-view]').forEach(function (b) { b.classList.remove('is-active'); });
    });

    // ---- resize (ResizeObserver so it self-heals if this section is
    // ever hidden/tabbed at init, same fix as the product-page chart) ----
    function resize() {
      var w = canvas.clientWidth, h = canvas.clientHeight;
      if (!w || !h) return;
      renderer.setSize(w, h, false);
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
    }
    window.addEventListener('resize', resize);
    resize();
    if (window.ResizeObserver) new ResizeObserver(resize).observe(canvas.parentElement);

    function animate() {
      requestAnimationFrame(animate);
      renderer.render(scene, camera);
    }
    animate();

    // ---- boot ----
    selectShape(state.shape);
  }

  whenThreeReady(function () {
    document.querySelectorAll('[data-cone-studio]').forEach(init);
  });
})();
